import { useEffect, useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Button } from '@components';
import { CONSTANT } from '@const';
import { WaterIQLogo } from '@public';
import QLDLogo from '@public/Qldlogo.png';
import { msalService } from '@services';
import { isEmpty } from 'lodash';
import { PuffLoader } from 'react-spinners';

import { b2cPolicies, msalConfig, protectedResources } from '@/authConfig';

import styles from './login.module.scss';

const LoginPage = () => {
	const router = useRouter();
	const { instance, inProgress } = useMsal();
	const [localSignInPolicyId] = useState(b2cPolicies.authorities.localAccount.authority);

	useEffect(() => {
		router.push('/');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function login() {
		localStorage.clear(); // Clear possible logout flag
		sessionStorage.clear(); // Clear possible selected client
		instance.loginRedirect();
	}

	function loginLocalAccount() {
		if (!localSignInPolicyId) {
			return;
		}

		localStorage.clear(); // Clear possible logout flag
		sessionStorage.clear(); // Clear possible selected client

		msalService.setAuthorityPolicy(localSignInPolicyId);

		const loginRequest = {
			...msalConfig,
			scopes: protectedResources.apiHello.scopes,
			authority: localSignInPolicyId,
		};
		instance.loginRedirect(loginRequest);
	}

	function SignInButton() {
		return (
			<Button
				customStyles={styles.loginButton}
				onClick={login}
				size="large"
				type="primary"
				tabIndex={1}
				autoFocus={true}
				aria-label="Login with Digital ID"
				data-testid="login"
			>
				Login with Digital ID
			</Button>
		);
	}

	const LocalSignInButton = () => {
		const canLoginWithLocalAccount =
			!isEmpty(localSignInPolicyId) && localSignInPolicyId !== 'undefined';
		return canLoginWithLocalAccount ? (
			<Button
				customStyles={styles.loginButton}
				onClick={loginLocalAccount}
				size="large"
				type="secondary"
				tabIndex={2}
				aria-label="Login with local account"
			>
				Login with local account
			</Button>
		) : (
			<></>
		);
	};

	return inProgress === InteractionStatus.None || inProgress === InteractionStatus.Startup ? (
		<div className={styles.wrapper}>
			<Head>
				<title>Login | WaterIQ</title>
				<meta name="description" content="Landing login page in WaterIQ" />
				<link rel="icon" type="image/x-icon" href="/favicon.ico" />
				<link rel="shortcut icon" type="image/x-icon" href="/favicon.ico" />
				<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
				<link rel="shortcut icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
				<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
				<link rel="shortcut icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
			</Head>
			<main className={styles.form}>
				<div className={styles.login}>
					<div className={styles.headerLogo}>
						<Image
							src={QLDLogo}
							alt="Queensland offical logo"
							width={160}
							height={200}
							priority
							data-testid="QLDLogo"
						/>
					</div>
					<p className={styles.appName} data-testid="waterIQTitle">
						<WaterIQLogo customStyles={styles.appLogo} />
						WaterIQ
					</p>
					{inProgress !== InteractionStatus.Startup ? (
						<>
							<SignInButton />
							<div className={styles.accountActions}>
								<p>
									<a
										className={styles.externalUrl}
										href={CONSTANT.FORGOTTEN_URL}
										tabIndex={2}
										data-testid="forgotPassword"
										target="_blank"
									>
										Forgot password?
									</a>
								</p>
							</div>
							<LocalSignInButton />
							<div className={styles.accountActions}>
								<p>
									<a
										className={styles.externalUrl}
										href={CONSTANT.DIGITAL_ID_FAQ}
										tabIndex={2}
										data-testid="forgotPassword"
										target="_blank"
									>
										Learn more about using a Digital ID
									</a>
								</p>
							</div>
						</>
					) : (
						<div className={styles.spinner}>
							<PuffLoader color="white" size={'50px'} />
						</div>
					)}
				</div>
			</main>
			{process.env.NEXT_PUBLIC_WIQ_ENVIRONMENT && (
				<div className={styles.footer}>
					<p>
						{process.env.NEXT_PUBLIC_WIQ_ENVIRONMENT}
						{' - '}
						{process.env.WIQ_BUILD_NUMBER || 'N/A'}
						{' - '}
						{process.env.WIQ_RELEASE_NUMBER || 'N/A'}
						{' - '}
						{process.env.WIQ_RELEASE_DATE || 'N/A'}
					</p>
				</div>
			)}
		</div>
	) : (
		<></>
	);
};

export default LoginPage;
